import { Head, Link, useForm } from '@inertiajs/react'
import { useEffect } from 'react'
import { Core as YubinBangoCore } from 'yubinbango-core2'
import BirthdayInput from '@/components/common/BirthdayInput'
import PageHeader from '@/components/common/PageHeader'

type Prefecture = {
  id: number
  name: string
}

type Props = {
  minimum_password_length: number
  prefectures: Prefecture[]
  referral_code: string
  // QR チラシ経由登録時のチェーン識別子（online_clinic_pharmacy_companies.id）。画面には出さず送信のみ
  pharmacy_company_id: number | null
}

const errorText = (error: string | string[] | undefined) => {
  if (!error) return ''
  return Array.isArray(error) ? error[0] : error
}

function FieldLabel({ text, required = true }: { text: string; required?: boolean }) {
  return (
    <div className="mb-1 flex items-center gap-2">
      <label className="text-sm font-medium text-[#2a3039]">{text}</label>
      {required ? (
        <span className="oc-badge-required">必須</span>
      ) : (
        <span className="oc-badge-optional">任意</span>
      )}
    </div>
  )
}

export default function V2SignUp({ minimum_password_length, prefectures, referral_code, pharmacy_company_id }: Props) {
  const form = useForm({
    first_name: '',
    first_name_kana: '',
    last_name: '',
    last_name_kana: '',
    birthday: '',
    sex: '',
    zip_code: '',
    address_prefecture_id: '',
    address_city: '',
    address_street: '',
    address_other: '',
    phone_number: '',
    email: '',
    password: '',
    password_confirmation: '',
    // 紹介リンク（?referral_code=）経由の紹介登録は入力欄なしで維持する
    referral_code: referral_code || '',
    pharmacy_company_id: pharmacy_company_id ?? '',
    agreement: false,
  })
  const errors = form.errors as typeof form.errors & { base?: string[] }

  useEffect(() => {
    if (!/^\d{7}$/.test(form.data.zip_code)) return

    new YubinBangoCore(form.data.zip_code, (addr: { region_id: number; locality: string; street: string; extended: string }) => {
      if (addr.region_id) {
        form.setData((prev) => ({
          ...prev,
          address_prefecture_id: String(addr.region_id),
          address_city: addr.locality,
          address_street: addr.street,
          address_other: addr.extended || prev.address_other,
        }))
      }
    })
  }, [form.data.zip_code])

  const submit = () => {
    form.transform((data) => ({ user: data }))
    form.post('/users?reservation=1')
  }

  return (
    <>
      <Head title="新規登録 - オンライン診療予約" />

      <div className="min-h-screen bg-white">
        <PageHeader subtitle="新規会員登録" />

        <main className="mx-auto max-w-3xl px-4 py-6">
          {/* 登録済みユーザー向けのログイン導線 (#1240)。従来は最下部のリンクのみで、
              登録済みの人が約1,500pxスクロールしないと気づけなかった。
              sign_in.tsx の「初めてご利用の方」カードと対になる逆方向の導線 */}
          <div className="oc-info-card mb-4 flex items-center justify-between gap-3 py-3">
            <p className="text-sm font-medium text-[#2a3039]">会員登録がお済みの方</p>
            <Link href="/users/sign_in?reservation=1" className="button-secondary h-[40px] w-auto shrink-0 px-5 text-[14px] font-bold">
              ログイン
            </Link>
          </div>

          <div className="oc-card space-y-4">
            <h1 className="oc-section-title">メールで登録する</h1>

            <div>
              <FieldLabel text="姓" />
              <input className="oc-input" value={form.data.first_name} onChange={(e) => form.setData('first_name', e.target.value)} />
              {form.errors.first_name && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.first_name)}</p>}
            </div>

            <div>
              <FieldLabel text="姓（フリガナ）" />
              <input className="oc-input" placeholder="ヤマダ" value={form.data.first_name_kana} onChange={(e) => form.setData('first_name_kana', e.target.value)} />
              {form.errors.first_name_kana && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.first_name_kana)}</p>}
            </div>

            <div>
              <FieldLabel text="名" />
              <input className="oc-input" value={form.data.last_name} onChange={(e) => form.setData('last_name', e.target.value)} />
              {form.errors.last_name && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.last_name)}</p>}
            </div>

            <div>
              <FieldLabel text="名（フリガナ）" />
              <input className="oc-input" placeholder="タロウ" value={form.data.last_name_kana} onChange={(e) => form.setData('last_name_kana', e.target.value)} />
              {form.errors.last_name_kana && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.last_name_kana)}</p>}
            </div>

            <div>
              <FieldLabel text="生年月日" />
              <BirthdayInput value={form.data.birthday} onChange={(value) => form.setData('birthday', value)} />
              {form.errors.birthday && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.birthday)}</p>}
            </div>

            <div>
              <FieldLabel text="性別" />
              <div className="space-y-2">
                <label className="flex items-center gap-3 text-[#2a3039]">
                  <input type="radio" className="oc-radio" checked={form.data.sex === 'male'} onChange={() => form.setData('sex', 'male')} />
                  男性
                </label>
                <label className="flex items-center gap-3 text-[#2a3039]">
                  <input type="radio" className="oc-radio" checked={form.data.sex === 'female'} onChange={() => form.setData('sex', 'female')} />
                  女性
                </label>
              </div>
              {form.errors.sex && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.sex)}</p>}
            </div>

            <div>
              <FieldLabel text="郵便番号" />
              <input className="oc-input" value={form.data.zip_code} onChange={(e) => form.setData('zip_code', e.target.value)} />
              {form.errors.zip_code && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.zip_code)}</p>}
            </div>

            <div>
              <FieldLabel text="住所（都道府県）" />
              <select className="oc-select" value={form.data.address_prefecture_id} onChange={(e) => form.setData('address_prefecture_id', e.target.value)}>
                <option value="">選択してください</option>
                {prefectures.map((prefecture) => (
                  <option key={prefecture.id} value={prefecture.id}>
                    {prefecture.name}
                  </option>
                ))}
              </select>
              {form.errors.address_prefecture_id && (
                <p className="mt-1 text-sm text-red-600">{errorText(form.errors.address_prefecture_id)}</p>
              )}
            </div>

            <div>
              <FieldLabel text="住所（市区郡町村）" />
              <input className="oc-input" value={form.data.address_city} onChange={(e) => form.setData('address_city', e.target.value)} />
              {form.errors.address_city && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.address_city)}</p>}
            </div>

            <div>
              <FieldLabel text="住所（町・番地）" />
              <input className="oc-input" value={form.data.address_street} onChange={(e) => form.setData('address_street', e.target.value)} />
              {form.errors.address_street && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.address_street)}</p>}
            </div>

            <div>
              <FieldLabel text="住所（建物名・部屋番号）" required={false} />
              <input className="oc-input" value={form.data.address_other} onChange={(e) => form.setData('address_other', e.target.value)} />
            </div>

            <div>
              <FieldLabel text="携帯番号" />
              <input
                className="oc-input"
                type="tel"
                inputMode="numeric"
                maxLength={11}
                value={form.data.phone_number}
                onChange={(e) => form.setData('phone_number', e.target.value)}
              />
              {form.errors.phone_number && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.phone_number)}</p>}
            </div>

            <div>
              <FieldLabel text="メールアドレス" />
              <input type="email" className="oc-input" value={form.data.email} onChange={(e) => form.setData('email', e.target.value)} />
              {form.errors.email && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.email)}</p>}
            </div>

            <div>
              <FieldLabel text="パスワード" />
              <input type="password" className="oc-input" value={form.data.password} onChange={(e) => form.setData('password', e.target.value)} />
              <p className="mt-1 text-xs text-[#7f8388]">{minimum_password_length}文字以上で設定</p>
              {form.errors.password && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.password)}</p>}
            </div>

            <div>
              <FieldLabel text="パスワード再入力" />
              <input
                type="password"
                className="oc-input"
                value={form.data.password_confirmation}
                onChange={(e) => form.setData('password_confirmation', e.target.value)}
              />
              {form.errors.password_confirmation && (
                <p className="mt-1 text-sm text-red-600">{errorText(form.errors.password_confirmation)}</p>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm text-[#2a3039]">
              <input
                type="checkbox"
                className="oc-checkbox mt-1"
                checked={form.data.agreement}
                onChange={(e) => form.setData('agreement', e.target.checked)}
              />
              <span>
                <a href="https://anamne.com/rules/" target="_blank" rel="noreferrer" className="oc-link">利用規約</a>
                と
                <a href="https://anamne.com/privacy/" target="_blank" rel="noreferrer" className="oc-link">プライバシーポリシー</a>
                に同意する
              </span>
            </label>
            {form.errors.agreement && <p className="mt-1 text-sm text-red-600">{errorText(form.errors.agreement)}</p>}

            {errors.base && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {errorText(errors.base)}
              </div>
            )}

            <button type="button" className="button-primary mt-2" disabled={form.processing} onClick={submit}>
              登録する
            </button>

            <div className="text-center text-sm">
              <Link href="/users/sign_in?reservation=1" className="oc-link">
                ログイン
              </Link>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
